import { useId, useRef } from 'react';
import ShowRow from './ShowRow';
import AssistantTextBubble from './AssistantTextBubble';
import { earlyShowDestination } from '../utils/chatEarlyShowMatch';
import './ChatEarlyShowMatch.css';

// The run controller owns acceptance, retention and reconciliation.
// This view keeps the same rows and expansion when the run finishes.
export default function ChatEarlyShowMatch({
    candidates = [],
    retained = false,
    expanded = false,
    onExpandedChange,
    onOpen,
    onInteractionChange,
}) {
    const choicesId = useId();
    const interaction = useRef({ focused: false, pointedAt: false });
    if (!candidates.length) return null;

    const multiple = candidates.length > 1;
    const visibleCandidates = expanded ? candidates : candidates.slice(0, 3);
    const updateInteraction = (change) => {
        interaction.current = { ...interaction.current, ...change };
        onInteractionChange?.(interaction.current);
    };

    return (
        <section className='chatEarlyShowMatch'
            aria-label={multiple ? 'Possible show matches' : 'Possible show match'}
            onFocusCapture={() => updateInteraction({ focused: true })}
            onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) updateInteraction({ focused: false });
            }}
            onPointerEnter={() => updateInteraction({ pointedAt: true })}
            onPointerLeave={() => updateInteraction({ pointedAt: false })}>
            <AssistantTextBubble className='chatEarlyShowMatchIntro'>
                {multiple ? 'I found a few shows you might be looking for.' : 'I found a show you might be looking for.'}
            </AssistantTextBubble>
            <ul className='chatEarlyShowChoices' id={choicesId}>
                {visibleCandidates.map((candidate) => {
                    const { show } = candidate;
                    const destination = earlyShowDestination(candidate);
                    const imageURL = typeof show.imageURL === 'string' ? show.imageURL.trim() : '';
                    return <li key={show.appleCollectionId || show.id || show.rssURL}>
                        <ShowRow show={{ ...show, imageURL, source: show.id ? show : undefined,
                            key: show.id ? `source:${show.id}` : show.appleCollectionId ? `apple:${show.appleCollectionId}` : `rss:${show.rssURL}` }}
                            description={show.creator}
                            openLabel={`View show: ${show.name}${multiple ? `, ${show.creator}` : ''}`}
                            disabled={!destination || typeof onOpen !== 'function'}
                            onOpen={() => onOpen(destination, candidate)} />
                    </li>;
                })}
            </ul>
            {candidates.length > 3 && <button type='button' className='chatEarlyShowExpand'
                aria-expanded={expanded} aria-controls={choicesId}
                aria-label={expanded ? 'Show fewer possible matches' : 'Show more possible matches'}
                disabled={typeof onExpandedChange !== 'function'} onClick={() => onExpandedChange(!expanded)}>
                {expanded ? 'Less' : 'More'}
            </button>}
            {!retained && <span className='chatEarlyShowAnnouncement' role='status' aria-live='polite'>
                {multiple ? `${candidates.length} possible show matches are ready.` : `A possible show match is ready: ${candidates[0].show.name}.`}
            </span>}
        </section>
    );
}
